import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import * as Constants from "common/constants";
import * as General from "common/general";
import { useTranslation } from "react-i18next";
import "../styles/Style.css";

const ImportWizard = () => {
	const [step, setStep] = useState(1);
	const [importTypes, setImportTypes] = useState([]);
	const [importType, setImportType] = useState(null);
	const [systemFields, setSystemFields] = useState([]);
	const [excelColumns, setExcelColumns] = useState([]);
	const [parsedData, setParsedData] = useState([]);
	const [fieldMapping, setFieldMapping] = useState({});
	const [loading, setLoading] = useState(false);
	const [fileName, setFileName] = useState("");
	const [validationErrors, setValidationErrors] = useState([]);

	// -----------------------------
	// Get Import Types
	// -----------------------------
	const getImportType = () => {
		const success = (responseObject) => {
			const data = responseObject.data.dataBaseContent;
			if (Array.isArray(data)) {
				setImportTypes(data);
			} else {
				setImportTypes([data]);
			}
		};
		const failure = (error) => {
			console.error("Import types error:", error);
		};
		const endPoint = Constants.GetAllEndPoint + "/tbl_import_type";
		General.GetFromEndPoint(endPoint, null, null, success, failure);
	};
	// -----------------------------
	// Get Config Import
	// -----------------------------
	const getConfigImport = (tableName) => {
		const success = (responseObject) => {
			const data = responseObject.data.dataBaseContent;
			if (Array.isArray(data)) {
				setSystemFields(data);
			} else {
				setSystemFields([data]);
			}
		};
		const failure = (error) => {
			console.error("Import config error:", error);
		};
		const endPoint = Constants.GetAllEndPoint + "/tbl_config_import/table_name=eq." + tableName;
		General.GetFromEndPoint(endPoint, null, null, success, failure);
	};
	useEffect(() => {
		getImportType();
	}, []);

	// -----------------------------
	// Upload Excel
	// -----------------------------
	const handleFileUpload = (file) => {
		if (!file) return;
		setFileName(file.name);
		const reader = new FileReader();
		reader.onload = (e) => {
			const data = new Uint8Array(e.target.result);
			const workbook = XLSX.read(data, { type: "array" });
			const worksheet = workbook.Sheets[workbook.SheetNames[0]];
			const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
			if (!json.length) {
				alert("Excel file empty");
				return;
			}
			const columns = Object.keys(json[0]);
			setExcelColumns(columns);
			setParsedData(json);
		};
		reader.readAsArrayBuffer(file);
	};
	// -----------------------------
	// Auto Mapping
	// -----------------------------
	useEffect(() => {
		if (!excelColumns.length || !systemFields.length) return;

		const autoMap = {};

		systemFields.forEach((field) => {
			const match = excelColumns.find((col) => col.toLowerCase() === field.column_key.toLowerCase());

			if (match) {
				autoMap[field.column_key] = match;
			}
		});

		setFieldMapping(autoMap);
	}, [excelColumns, systemFields]);

	//------------------------------
	//normalize Fields
	//------------------------------
	const normalizeFieldValue = (value, fieldConfig) => {
		if (!value) return value;

		let v = String(value).trim();

		if (fieldConfig.replaceSpacesWith) {
			v = v.replace(/\s+/g, fieldConfig.replaceSpacesWith);
		}

		if (fieldConfig.to_lower === "True") {
			v = v.toLowerCase();
		}

		if (fieldConfig.to_upper === "True") {
			v = v.toUpperCase();
		}

		return v;
	};
	//------------------------------
	//validation
	//------------------------------

	const [t, i18n] = useTranslation();
	const validateRows = () => {
		const errors = [];

		// 🔴 Track duplicates per field
		const uniqueTracker = {};

		mappedData.forEach((row, rowIndex) => {
			systemFields.forEach((field) => {
				const value = row[field.column_key];
				const rules = field || {};

				// REQUIRED
				if (rules.required === "True" && (!value || value === "")) {
					errors.push({
						row: rowIndex + 2,
						field: field.column_key,
						message: "Required field",
					});
					return;
				}

				// 🔴 UNIQUE (collect first)
				if (rules.unique === "True" && value) {
					if (!uniqueTracker[field.column_key]) {
						uniqueTracker[field.column_key] = new Map();
					}

					const fieldMap = uniqueTracker[field.column_key];

					if (!fieldMap.has(value)) {
						fieldMap.set(value, []);
					}

					fieldMap.get(value).push(rowIndex + 2);
				}

				// REGEX
				if (rules.regex && value) {
					const regex = new RegExp(rules.regex);
					if (!regex.test(value)) {
						errors.push({
							row: rowIndex + 2,
							field: field.column_key,
							message: "Invalid format",
						});
					}
				}

				// MIN LENGTH
				if (rules.min && value && String(value).length < parseInt(rules.min)) {
					errors.push({
						row: rowIndex + 2,
						field: field.column_key,
						message: `Minimum length ${rules.min}`,
					});
				}

				// MAX LENGTH
				if (rules.max && value && String(value).length > parseInt(rules.max)) {
					errors.push({
						row: rowIndex + 2,
						field: field.column_key,
						message: `Maximum length ${rules.max}`,
					});
				}

				// EQUAL LENGTH
				if (rules.equal && value && String(value).length !== parseInt(rules.equal)) {
					errors.push({
						row: rowIndex + 2,
						field: field.column_key,
						message: `Must be exactly ${rules.equal} characters`,
					});
				}
			});
		});

		// 🔴 Detect duplicates AFTER loop
		Object.keys(uniqueTracker).forEach((field) => {
			const fieldMap = uniqueTracker[field];

			fieldMap.forEach((rows, value) => {
				if (rows.length > 1) {
					rows.forEach((rowNumber) => {
						errors.push({
							row: rowNumber,
							field: field,
							message: `Duplicate value (${value})`,
						});
					});
				}
			});
		});

		return errors;
	};
	useEffect(() => {
		if (step === 3 && mappedData.length > 0) {
			const errors = validateRows();
			setValidationErrors(errors);
		}
	}, [step, mappedData]);

	// -----------------------------
	// Mapping Change
	// -----------------------------

	const isMappingValid = () => {
		const requiredFields = systemFields.filter((f) => f.required === "True");

		return requiredFields.every((field) => fieldMapping[field.column_key]);
	};
	const handleMappingChange = (field, column) => {
		setFieldMapping({
			...fieldMapping,
			[field]: column,
		});
	};
	// -----------------------------
	// Build mapped data
	// -----------------------------
	const getMappedData = () => {
		let extraFields = {};

		if (importType?.extra_fields != null) {
			const companyInfo = JSON.parse(localStorage.getItem("loggedInUserInfo"));

			extraFields = {
				transporterCompanyId: companyInfo?.companyId,
			};
		}

		return parsedData.map((row) => {
			const newRow = {};

			systemFields.forEach((field) => {
				const excelColumn = fieldMapping[field.column_key];

				if (!excelColumn) return;

				let value = row[excelColumn];

				value = normalizeFieldValue(value, field);

				newRow[field.column_key] = value;
			});

			return { ...newRow, ...extraFields };
		});
	};

	const mappedData = React.useMemo(() => {
		return getMappedData();
	}, [parsedData, fieldMapping, systemFields]);
	// -----------------------------
	// Upload
	// -----------------------------
	const handleUpload = async () => {
		const errors = validateRows();

		if (errors.length > 0) {
			console.log(errors);
			alert(`Error in row ${errors[0].row} field ${errors[0].field}`);
			return;
		}
		try {
			let payload = {
				TableName: importType.table_name,
				extra_table: importType?.extra_table,
				data: mappedData,
			};
			console.log(payload);
			setLoading(true);
			const response = await General.MassImport(payload);
			console.log({ response });
			if (response.httpResponse.status == 0) {
				alert("Import Failed");
				return 0;
			}
			alert(`Import successful
      ${response.httpResponse.message}`);
			setStep(1);
			setParsedData([]);
			setExcelColumns([]);
			setFieldMapping({});
			setImportType(null);
		} catch (err) {
			alert("Import failed");
		} finally {
			setLoading(false);
		}
	};
	return (
		<div className="container mt-5">
			<h3 className="mb-4">{t("Data Import Wizard")}</h3>
			{/* Stepper */}
			<div className="wizard-stepper mb-5">
				<div className={`step ${step >= 1 ? "active" : ""}`}>
					<div className="circle">1</div>
					<div className="label">{t("Select Type")}</div>
				</div>
				<div className="line"></div>
				<div className={`step ${step >= 2 ? "active" : ""}`}>
					<div className="circle">2</div>
					<div className="label">{t("Upload & Map")}</div>
				</div>
				<div className="line"></div>
				<div className={`step ${step >= 3 ? "active" : ""}`}>
					<div className="circle">3</div>
					<div className="label">{t("Confirm")}</div>
				</div>
			</div>
			{/* STEP 1 */}
			{step === 1 && (
				<div className="card p-4 shadow-sm">
					<h5>{t("Select Import Type")}</h5>
					<select
						className="form-control mt-3"
						value={importType?.table_name || ""}
						onChange={(e) => {
							const type = importTypes.find((t) => t.table_name === e.target.value);
							setImportType(type);
							if (type) {
								getConfigImport(type.id);
							}
						}}
					>
						<option value="">{t("Select table...")}</option>
						{importTypes.map((a) => (
							<option key={a.id} value={a.table_name}>
								{t(a.label)}
							</option>
						))}
					</select>
					<button className="btn btn-primary mt-4" disabled={!importType} onClick={() => setStep(2)}>
						{t("Next")}
					</button>
				</div>
			)}
			{/* STEP 2 */}
			{step === 2 && (
				<div className="card p-4 shadow-sm">
					<h5>{t("Upload Excel")}</h5>
					<div
						className="upload-zone mt-3"
						onDragOver={(e) => e.preventDefault()}
						onDrop={(e) => {
							e.preventDefault();
							handleFileUpload(e.dataTransfer.files[0]);
						}}
					>
						{fileName ? (
							<>
								<p className="fw-bold text-success">✅ {fileName}</p>
								<p className="text-muted">{t("File uploaded successfully")}</p>
							</>
						) : (
							<>
								<p>{t("📂 Drag & Drop Excel File")}</p>
								<p className="text-muted">{t("or click to browse")}</p>
							</>
						)}
						<input type="file" accept=".xlsx,.xls" onChange={(e) => handleFileUpload(e.target.files[0])} />
					</div>
					{excelColumns.length > 0 && (
						<div className="mt-4">
							<h6>{t("Field Mapping")}</h6>
							{systemFields.map((field) => (
								<div key={field.id} className="mapping-row">
									<div className="excel-col">
										<select className="form-select" value={fieldMapping[field.column_key] || ""} onChange={(e) => handleMappingChange(field.column_key, e.target.value)}>
											<option value="">{t("Select Excel Column")}</option>
											{excelColumns.map((col) => (
												<option key={col} value={col}>
													{col}
												</option>
											))}
										</select>
									</div>
									<div className="arrow">→</div>
									<div className="system-col">
										<b>{field.column_key}</b>
										{field.required == "True" && <span className="text-danger ms-1">*</span>}
									</div>
								</div>
							))}
						</div>
					)}
					<div className="mt-4">
						<button className="btn btn-secondary" onClick={() => setStep(1)}>
							{t("Back")}
						</button>
						<button className="btn btn-primary ms-2" disabled={!isMappingValid()} onClick={() => setStep(3)}>
							{t("Next")}
						</button>
					</div>
				</div>
			)}
			{/* STEP 3 */}
			{step === 3 && mappedData.length > 0 && (
				<div className="card p-4 shadow-sm">
					<h5>{t("Confirm Import")}</h5>
					<div className="alert alert-info">
						{t("Rows detected:")} <b>{mappedData.length}</b>
						<br />
						{t("Mapped fields:")} <b>{Object.keys(fieldMapping).length}</b>
						<br />
						{t("Target table:")} <b>{importType.label}</b>
					</div>
					<div className="table-responsive">
						<table className="table table-bordered">
							<thead>
								<tr>
									{Object.keys(mappedData[0]).map((col) => (
										<th key={col}>{col}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{mappedData.slice(0, 10).map((row, i) => (
									<tr key={i}>
										{Object.entries(row).map(([col, value], j) => {
											const hasError = validationErrors.some((err) => err.row === i + 2 && err.field === col);
											return (
												<td
													key={j}
													style={{
														backgroundColor: hasError ? "#f8d7da" : "transparent",
														color: hasError ? "#721c24" : "inherit",
													}}
													title={hasError ? validationErrors.find((err) => err.row === i + 2 && err.field === col).message : ""}
												>
													{value}
												</td>
											);
										})}
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div className="mt-4">
						<button className="btn btn-secondary" onClick={() => setStep(2)}>
							{t("Back")}
						</button>
						<button className="btn btn-primary ms-2" onClick={handleUpload} disabled={loading}>
							{loading ? t("Importing...") : t("Confirm Import")}
						</button>
					</div>
				</div>
			)}
		</div>
	);
};
export default ImportWizard;
